import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import SocialReportType from '@/Enums/ReportingDashboard/Social/ReportType.enum';
import ReportRequest from '@/Enums/ReportingDashboard/SocialReportRequest.enum';
import type {
  IBaseDataReport,
  ICampaignReport,
  IDateReport,
  ITotalReport,
} from '@/Interfaces/ReportingDashboard/Social/Report.interface';
import type {
  IAdNameReportRecord,
  IAdvertiserReportRecord,
  IAgencyReportRecord,
  IBaseReportRecord,
  IPlacementReportRecord,
  IPlatformReportRecord,
} from '@/Interfaces/ReportingDashboard/Social/ReportRecord.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Social/State.interface';
import { defaultTotals } from '@/constants/reportingDashboard/social';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { socialReportRequestHandler } from '@/helpers/reportingDashboard/social';
import type { RootState } from '@/store';
import type { SocialReportView } from '@/types/ReportingDashboard/Social.type';
import type { RequestMap } from '@/types/RequestMap.types';
import { AsyncThunk, AsyncThunkOptions, createAsyncThunk } from '@reduxjs/toolkit';

enum ActionPrefix {
  Totals = 'reportingDashboardSocial/getTotalsReport',

  DateReport = 'reportingDashboardSocial/getDateReport',
  PlatformReport = 'reportingDashboardSocial/getPlatformReport',
  AgencyReport = 'reportingDashboardSocial/getAgencyReport',
  AdvertiserReport = 'reportingDashboardSocial/getAdvertiserReport',
  CampaignReport = 'reportingDashboardSocial/getCampaignReport',
  AdNameReport = 'reportingDashboardSocial/getAdNameReport',
  PlacementReport = 'reportingDashboardSocial/getPlacementReport',
}

const shouldFetchExplicitReport = (report: IBaseReportRecord[], requestId: string | null) => {
  if (requestId === DEFAULT_LOADING_REQUEST_ID) {
    return true;
  }
  return !report?.length && !requestId;
};

const getExplicitReportThunkOptions = (
  dataField: keyof IState,
  requestIdField?: keyof IState,
): AsyncThunkOptions<IAbortSignalThunkArg> => ({
  condition: (_, { getState }) => {
    const requestIdKey = requestIdField ?? (`${dataField}RequestId` as keyof IState);
    const {
      reportingDashboardSocial: { [dataField]: data, [requestIdKey]: requestId },
    } = getState() as RootState;

    return shouldFetchExplicitReport(data as IBaseReportRecord[], requestId as string);
  },
});

export const getTotalReport = createAsyncThunk<ITotalReport, IAbortSignalThunkArg>(
  ActionPrefix.Totals,
  socialReportRequestHandler(SocialReportType.Total, { totals: defaultTotals }),
);

//#region grid
export const getDateReport = createAsyncThunk<IDateReport, IAbortSignalThunkArg>(
  ActionPrefix.DateReport,
  socialReportRequestHandler(SocialReportType.Date),
  getExplicitReportThunkOptions('dateReport'),
);

export const getPlatformReport = createAsyncThunk<IBaseDataReport<IPlatformReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.PlatformReport,
  socialReportRequestHandler(SocialReportType.Platform),
  getExplicitReportThunkOptions('platformReport'),
);

export const getAdvertiserReport = createAsyncThunk<IBaseDataReport<IAdvertiserReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.AdvertiserReport,
  socialReportRequestHandler(SocialReportType.Advertiser),
  getExplicitReportThunkOptions('advertiserReport'),
);

export const getAgencyReport = createAsyncThunk<IBaseDataReport<IAgencyReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.AgencyReport,
  socialReportRequestHandler(SocialReportType.Agency),
  getExplicitReportThunkOptions('agencyReport'),
);

export const getCampaignReport = createAsyncThunk<ICampaignReport, IAbortSignalThunkArg>(
  ActionPrefix.CampaignReport,
  socialReportRequestHandler(SocialReportType.Campaign),
  getExplicitReportThunkOptions('campaignReport'),
);

export const getAdNameReport = createAsyncThunk<IBaseDataReport<IAdNameReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.AdNameReport,
  socialReportRequestHandler(SocialReportType.AdName),
  getExplicitReportThunkOptions('adNameReport'),
);

export const getPlacementReport = createAsyncThunk<IBaseDataReport<IPlacementReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.PlacementReport,
  socialReportRequestHandler(SocialReportType.Placement),
  getExplicitReportThunkOptions('placementReport'),
);
//#endregion

//#region dispatch map
type ActionSet = { action: AsyncThunk<any, any, any>; reportRequest: ReportRequest };

export const reportDispatch: Record<SocialReportView, ActionSet> = {
  [ReportView.Date]: { action: getDateReport, reportRequest: ReportRequest.Date },
  [ReportView.Platform]: { action: getPlatformReport, reportRequest: ReportRequest.Platform },
  [ReportView.Agency]: { action: getAgencyReport, reportRequest: ReportRequest.Agency },
  [ReportView.Advertiser]: { action: getAdvertiserReport, reportRequest: ReportRequest.Advertiser },
  [ReportView.Campaign]: { action: getCampaignReport, reportRequest: ReportRequest.Campaign },
  [ReportView.AdName]: { action: getAdNameReport, reportRequest: ReportRequest.AdName },
  [ReportView.Placement]: { action: getPlacementReport, reportRequest: ReportRequest.Placement },
};

//#endregion

//#region reducers
export const reportMap: RequestMap<IState> = [
  ['dateReport', getDateReport],
  ['platformReport', getPlatformReport],
  ['agencyReport', getAgencyReport],
  ['advertiserReport', getAdvertiserReport],
  ['adNameReport', getAdNameReport],
  ['placementReport', getPlacementReport],
];
//#endregion
